import type { PluginOption, ViteDevServer } from 'vite';
import type { ProjectModel } from '../../../model.js';
import type { ProjectModelWatcher } from '../../../app-manifest/project-model-watcher.js';
import {
  virtualCustomElementPanelScriptAssets,
  virtualCustomElementScriptAssets,
  virtualBlocksWidgetPanelScriptAssets,
  virtualBlocksWidgetScriptAssets,
  virtualSiteComponentScriptAssets,
  virtualSiteComponentSdkScriptAssets,
  virtualSiteComponentPanelScriptAssets,
  virtualSiteComponentPreviewScriptAssets,
} from '../../../dev-center/components/site/config.js';
import { virtualDashboardScriptAssets } from '../../../dev-center/components/dashboard/config.js';
import {
  getBlocksWidgetDevTemplate,
  getBlocksPanelDevTemplate,
  getBlocksWidgetBuildTemplate,
  getBlocksPanelBuildTemplate,
} from './blocks-components.js';
import {
  getDashboardComponentBuildTemplate,
  getDashboardComponentDevTemplate,
} from './dashboard-component.js';
import {
  getCustomElementBuildTemplate,
  getCustomElementDevTemplate,
  getCustomElementPanelBuildTemplate,
  getCustomElementPanelDevTemplate,
} from './custom-elements.js';
import {
  getSiteComponentDevTemplate,
  getSiteComponentBuildTemplate,
  getSiteComponentSdkDevTemplate,
  getSiteComponentSdkBuildTemplate,
  getSiteComponentPanelBuildTemplate,
  getSiteComponentPanelDevTemplate,
  getSiteComponentPreviewBuildTemplate,
  getSiteComponentPreviewDevTemplate,
} from './site-components.js';
import { getViteServerUrl } from '../../vite-server-url.js';

const postfixRE = /[?#].*$/s;
const cleanId = (url: string) => url.replace(postfixRE, '');

export const moduleBundleDev = ({
  initialModel,
  projectModelWatcher,
}: {
  initialModel: ProjectModel;
  projectModelWatcher: ProjectModelWatcher;
}): PluginOption => {
  const { appId } = initialModel.config;

  let virtualDashboardAssets = virtualDashboardScriptAssets(initialModel);
  let virtualCustomElementAssets =
    virtualCustomElementScriptAssets(initialModel);
  let virtualCustomElementPanelAssets =
    virtualCustomElementPanelScriptAssets(initialModel);
  let virtualBlocksWidgetAssets = virtualBlocksWidgetScriptAssets(initialModel);
  let virtualBlocksWidgetPanelAssets =
    virtualBlocksWidgetPanelScriptAssets(initialModel);
  let virtualSiteComponentPanelAssets =
    virtualSiteComponentPanelScriptAssets(initialModel);

  let serverInstance: ViteDevServer;

  return {
    name: 'wix:module-bundle-dev',
    apply: 'serve',
    resolveId(rawId) {
      const id = cleanId(rawId);
      if (
        virtualDashboardAssets.has(id) ||
        virtualCustomElementAssets.has(id) ||
        virtualCustomElementPanelAssets.has(id) ||
        virtualBlocksWidgetAssets.has(id) ||
        virtualBlocksWidgetPanelAssets.has(id) ||
        virtualSiteComponentPanelAssets.has(id)
      ) {
        return id;
      }

      return null;
    },
    configureServer(server) {
      serverInstance = server;

      projectModelWatcher.onChange((model) => {
        virtualDashboardAssets = virtualDashboardScriptAssets(model);
        for (const scriptId of virtualDashboardAssets.keys()) {
          const moduleBundle = server.moduleGraph.getModuleById(scriptId);
          if (moduleBundle) {
            server.moduleGraph.invalidateModule(moduleBundle);
          }
        }

        virtualCustomElementAssets = virtualCustomElementScriptAssets(model);
        for (const scriptId of virtualCustomElementAssets.keys()) {
          const moduleBundle = server.moduleGraph.getModuleById(scriptId);
          if (moduleBundle) {
            server.moduleGraph.invalidateModule(moduleBundle);
          }
        }

        virtualCustomElementPanelAssets =
          virtualCustomElementPanelScriptAssets(model);
        for (const scriptId of virtualCustomElementPanelAssets.keys()) {
          const moduleBundle = server.moduleGraph.getModuleById(scriptId);
          if (moduleBundle) {
            server.moduleGraph.invalidateModule(moduleBundle);
          }
        }

        virtualBlocksWidgetAssets = virtualBlocksWidgetScriptAssets(model);
        for (const scriptId of virtualBlocksWidgetAssets.keys()) {
          const moduleBundle = server.moduleGraph.getModuleById(scriptId);
          if (moduleBundle) {
            server.moduleGraph.invalidateModule(moduleBundle);
          }
        }

        virtualBlocksWidgetPanelAssets =
          virtualBlocksWidgetPanelScriptAssets(model);
        for (const scriptId of virtualBlocksWidgetPanelAssets.keys()) {
          const moduleBundle = server.moduleGraph.getModuleById(scriptId);
          if (moduleBundle) {
            server.moduleGraph.invalidateModule(moduleBundle);
          }
        }

        virtualSiteComponentPanelAssets =
          virtualSiteComponentPanelScriptAssets(model);
        for (const scriptId of virtualSiteComponentPanelAssets.keys()) {
          const moduleBundle = server.moduleGraph.getModuleById(scriptId);
          if (moduleBundle) {
            server.moduleGraph.invalidateModule(moduleBundle);
          }
        }
      });
    },
    load(rawId) {
      const id = cleanId(rawId);

      const dashboardComponent = virtualDashboardAssets.get(id);
      if (dashboardComponent) {
        return getDashboardComponentDevTemplate(appId, dashboardComponent);
      }

      const customElementComponent = virtualCustomElementAssets.get(id);

      if (customElementComponent) {
        return getCustomElementDevTemplate(
          appId,
          getViteServerUrl(serverInstance),
          customElementComponent
        );
      }

      const customElementPanelComponent =
        virtualCustomElementPanelAssets.get(id);
      if (customElementPanelComponent) {
        return getCustomElementPanelDevTemplate(customElementPanelComponent);
      }

      const blocksWidgetComponent = virtualBlocksWidgetAssets.get(id);
      if (blocksWidgetComponent) {
        return getBlocksWidgetDevTemplate(blocksWidgetComponent);
      }

      const blocksPanelComponent = virtualBlocksWidgetPanelAssets.get(id);
      if (blocksPanelComponent) {
        return getBlocksPanelDevTemplate(blocksPanelComponent);
      }

      const siteComponentPanel = virtualSiteComponentPanelAssets.get(id);
      if (siteComponentPanel) {
        return getSiteComponentPanelDevTemplate(siteComponentPanel);
      }

      return null;
    },
  };
};

export const moduleBundleDevSiteComponents = ({
  initialModel,
  projectModelWatcher,
}: {
  initialModel: ProjectModel;
  projectModelWatcher: ProjectModelWatcher;
}): PluginOption => {
  const { appId } = initialModel.config;

  let virtualSiteComponentAssets =
    virtualSiteComponentScriptAssets(initialModel);
  let virtualSiteComponentPreviewAssets =
    virtualSiteComponentPreviewScriptAssets(initialModel);
  let virtualSiteComponentSdkAssets =
    virtualSiteComponentSdkScriptAssets(initialModel);

  return {
    name: 'wix:module-bundle-dev',
    apply: 'serve',
    resolveId(rawId) {
      const id = cleanId(rawId);
      if (
        virtualSiteComponentAssets.has(id) ||
        virtualSiteComponentPreviewAssets.has(id) ||
        virtualSiteComponentSdkAssets.has(id)
      ) {
        return id;
      }

      return null;
    },
    configureServer(server) {
      projectModelWatcher.onChange((model) => {
        virtualSiteComponentAssets = virtualSiteComponentScriptAssets(model);
        for (const scriptId of virtualSiteComponentAssets.keys()) {
          const moduleBundle = server.moduleGraph.getModuleById(scriptId);
          if (moduleBundle) {
            server.moduleGraph.invalidateModule(moduleBundle);
          }
        }

        virtualSiteComponentPreviewAssets =
          virtualSiteComponentPreviewScriptAssets(model);
        for (const scriptId of virtualSiteComponentPreviewAssets.keys()) {
          const moduleBundle = server.moduleGraph.getModuleById(scriptId);
          if (moduleBundle) {
            server.moduleGraph.invalidateModule(moduleBundle);
          }
        }

        virtualSiteComponentSdkAssets =
          virtualSiteComponentSdkScriptAssets(model);
        for (const scriptId of virtualSiteComponentSdkAssets.keys()) {
          const moduleBundle = server.moduleGraph.getModuleById(scriptId);
          if (moduleBundle) {
            server.moduleGraph.invalidateModule(moduleBundle);
          }
        }
      });
    },
    load(rawId) {
      const id = cleanId(rawId);

      const siteComponent = virtualSiteComponentAssets.get(id);
      if (siteComponent) {
        return getSiteComponentDevTemplate(appId, siteComponent);
      }

      const siteComponentPreview = virtualSiteComponentPreviewAssets.get(id);
      if (siteComponentPreview) {
        return getSiteComponentPreviewDevTemplate(appId, siteComponentPreview);
      }

      const siteSdkComponent = virtualSiteComponentSdkAssets.get(id);
      if (siteSdkComponent) {
        return getSiteComponentSdkDevTemplate(siteSdkComponent);
      }

      return null;
    },
  };
};

export const moduleBundleBuildBrowser = ({
  initialModel,
}: {
  initialModel: ProjectModel;
}): PluginOption => {
  const { appId } = initialModel.config;

  const virtualDashboardAssets = virtualDashboardScriptAssets(initialModel);
  const virtualCustomElementAssets =
    virtualCustomElementScriptAssets(initialModel);
  const virtualCustomElementPanelAssets =
    virtualCustomElementPanelScriptAssets(initialModel);
  const virtualSiteComponentSdkAssets =
    virtualSiteComponentSdkScriptAssets(initialModel);
  const virtualSiteComponentPreviewAssets =
    virtualSiteComponentPreviewScriptAssets(initialModel);
  const virtualSiteComponentPanelAssets =
    virtualSiteComponentPanelScriptAssets(initialModel);

  return {
    name: 'wix:module-bundle-build-browser',
    apply: 'build',
    resolveId(rawId) {
      const id = cleanId(rawId);
      if (
        virtualDashboardAssets.has(id) ||
        virtualCustomElementAssets.has(id) ||
        virtualCustomElementPanelAssets.has(id) ||
        virtualSiteComponentSdkAssets.has(id) ||
        virtualSiteComponentPreviewAssets.has(id) ||
        virtualSiteComponentPanelAssets.has(id)
      ) {
        return id;
      }

      return null;
    },
    load(rawId) {
      const id = cleanId(rawId);

      const dashboardComponent = virtualDashboardAssets.get(id);
      if (dashboardComponent) {
        return getDashboardComponentBuildTemplate(appId, dashboardComponent);
      }

      const customElementComponent = virtualCustomElementAssets.get(id);
      if (customElementComponent) {
        return getCustomElementBuildTemplate(appId, customElementComponent);
      }

      const customElementPanelComponent =
        virtualCustomElementPanelAssets.get(id);
      if (customElementPanelComponent) {
        return getCustomElementPanelBuildTemplate(customElementPanelComponent);
      }

      const siteSdkComponent = virtualSiteComponentSdkAssets.get(id);
      if (siteSdkComponent) {
        return getSiteComponentSdkBuildTemplate(siteSdkComponent);
      }

      const siteComponentPanel = virtualSiteComponentPanelAssets.get(id);
      if (siteComponentPanel) {
        return getSiteComponentPanelBuildTemplate(siteComponentPanel);
      }

      return null;
    },
  };
};

export const moduleBundleBuildBlocks = ({
  initialModel,
}: {
  initialModel: ProjectModel;
}): PluginOption => {
  const virtualBlocksWidgetAssets =
    virtualBlocksWidgetScriptAssets(initialModel);
  const virtualBlocksWidgetPanelAssets =
    virtualBlocksWidgetPanelScriptAssets(initialModel);

  return {
    name: 'wix:module-bundle-build-blocks',
    apply: 'build',
    resolveId(rawId) {
      const id = cleanId(rawId);
      if (
        virtualBlocksWidgetAssets.has(id) ||
        virtualBlocksWidgetPanelAssets.has(id)
      ) {
        return id;
      }

      return null;
    },
    load(rawId) {
      const id = cleanId(rawId);

      const blocksWidgetComponent = virtualBlocksWidgetAssets.get(id);
      if (blocksWidgetComponent) {
        return getBlocksWidgetBuildTemplate(blocksWidgetComponent);
      }

      const blocksPanelComponent = virtualBlocksWidgetPanelAssets.get(id);
      if (blocksPanelComponent) {
        return getBlocksPanelBuildTemplate(blocksPanelComponent);
      }

      return null;
    },
  };
};

export const moduleBundleBuildSiteComponents = ({
  initialModel,
}: {
  initialModel: ProjectModel;
}): PluginOption => {
  const { appId } = initialModel.config;
  const virtualSiteComponentAssets =
    virtualSiteComponentScriptAssets(initialModel);
  const virtualSiteComponentPreviewAssets =
    virtualSiteComponentPreviewScriptAssets(initialModel);
  const virtualSiteComponentSdkAssets =
    virtualSiteComponentSdkScriptAssets(initialModel);

  return {
    name: 'wix:module-bundle-build-site-components',
    apply: 'build',
    resolveId(rawId) {
      const id = cleanId(rawId);
      if (
        virtualSiteComponentAssets.has(id) ||
        virtualSiteComponentPreviewAssets.has(id) ||
        virtualSiteComponentSdkAssets.has(id)
      ) {
        return id;
      }

      return null;
    },
    load(rawId) {
      const id = cleanId(rawId);

      const siteComponent = virtualSiteComponentAssets.get(id);
      if (siteComponent) {
        return getSiteComponentBuildTemplate(appId, siteComponent);
      }

      const siteComponentPreview = virtualSiteComponentPreviewAssets.get(id);
      if (siteComponentPreview) {
        return getSiteComponentPreviewBuildTemplate(
          appId,
          siteComponentPreview
        );
      }

      const siteSdkComponent = virtualSiteComponentSdkAssets.get(id);
      if (siteSdkComponent) {
        return getSiteComponentSdkBuildTemplate(siteSdkComponent);
      }

      return null;
    },
  };
};
